// Phase row — collapse-chevron, inline-edit name, status badge,
// progress (done / total), and a drag-handle area. Hover-actions: rename, delete.

import { useState } from "preact/hooks";
import { ChevronDown, ChevronRight, GripVertical, Plus, Trash2 } from "lucide-preact";
import type { Phase } from "@juuplan/shared";
import { InlineEdit } from "./InlineEdit";
import { PHASE_STATUS_LABEL } from "../lib/format";

interface Props {
  phase: Phase;
  collapsed: boolean;
  taskTotal: number;
  taskDone: number;
  onToggle: () => void;
  onRename: (next: string) => void | Promise<void>;
  onAddTask: () => void;
  onDelete: () => void;
  /** Drag-handle props from dnd context (spread on the handle). */
  dragHandleProps?: Record<string, unknown>;
  /** Viewer-Rolle: Drag-Handle, Rename, Add-Task, Delete ausblenden. */
  readOnly?: boolean;
}

export function PhaseHeader(props: Props): preact.JSX.Element {
  const {
    phase,
    collapsed,
    taskTotal,
    taskDone,
    onToggle,
    onRename,
    onAddTask,
    onDelete,
    dragHandleProps,
    readOnly = false,
  } = props;
  const [confirmDel, setConfirmDel] = useState(false);

  const pct = taskTotal > 0 ? Math.round((taskDone / taskTotal) * 100) : 0;

  return (
    <div
      class="juuplan-phase-header group flex items-center gap-2 px-3 py-3 bg-[color:var(--color-surface-container)] border border-[color:var(--color-outline-variant)] rounded sticky top-0 z-10"
    >
      {!readOnly && (
        <span
          {...(dragHandleProps ?? {})}
          class="cursor-grab text-[color:var(--color-on-surface-variant)] opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Phase verschieben"
          title="Phase verschieben"
        >
          <GripVertical size={14} aria-hidden="true" />
        </span>
      )}
      <button
        type="button"
        onClick={onToggle}
        class="rounded p-1 hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
        style={{ color: phase.color ?? "var(--color-primary)" }}
        aria-label={collapsed ? "Phase ausklappen" : "Phase einklappen"}
        aria-expanded={!collapsed}
      >
        {collapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />}
      </button>
      <span
        class="inline-block size-2 rounded-full shrink-0"
        style={{ background: phase.color ?? "var(--color-primary)" }}
        aria-hidden="true"
      />
      <InlineEdit
        value={phase.name}
        onSave={onRename}
        class="juuplan-phase-name text-lg font-semibold text-[color:var(--color-on-surface)] flex-1 min-w-0"
        ariaLabel="Phasen-Namen bearbeiten"
        readOnly={readOnly}
      />

      <span class="text-label text-[color:var(--color-on-surface-variant)] hidden sm:inline">
        {phase.status ? PHASE_STATUS_LABEL[phase.status] : "—"}
      </span>

      <div class="flex items-center gap-2 shrink-0">
        {/* Mobile: compact done/total count so the phase still shows progress. */}
        <span class="sm:hidden text-micro tabular-nums text-[color:var(--color-on-surface-variant)]">
          {taskDone}/{taskTotal}
        </span>
        <div class="hidden sm:flex items-center gap-1 text-label text-[color:var(--color-on-surface-variant)]">
          <span>
            {taskDone}/{taskTotal}
          </span>
          <div
            class="h-1.5 w-16 rounded bg-[color:var(--color-surface-container-low)] overflow-hidden"
            aria-label={`Fortschritt ${pct}%`}
          >
            <div
              class="h-full"
              style={{
                width: `${pct}%`,
                background: phase.color ?? "var(--color-primary)",
              }}
            />
          </div>
        </div>

        {!readOnly && (
          <button
            type="button"
            onClick={onAddTask}
            class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
            aria-label="Aufgabe hinzufügen"
            title="Aufgabe hinzufügen"
          >
            <Plus size={14} aria-hidden="true" />
          </button>
        )}

        {readOnly ? null : confirmDel ? (
          <span class="inline-flex flex-wrap items-center gap-1 text-label">
            <button
              type="button"
              onClick={() => {
                setConfirmDel(false);
                onDelete();
              }}
              class="shrink-0 rounded bg-[color:var(--color-error)] px-2 py-1 text-[color:var(--color-on-error)] font-medium hover:opacity-90 transition-opacity"
            >
              Wirklich löschen
            </button>
            <button
              type="button"
              onClick={() => setConfirmDel(false)}
              class="shrink-0 rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] transition-colors"
            >
              Abbrechen
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDel(true)}
            class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all"
            aria-label="Phase löschen"
            title="Phase löschen"
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
